/*eslint-disable*/
import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@mui/styles";
import { List, ListItem, Tooltip, Box, Typography, Menu, MenuItem } from "@mui/material";
import { Home, User, Info, Mail, Instagram, Globe, ChevronDown, Sun, Moon } from "lucide-react";
import Button from "components/CustomButtons/Button.js";
import styles from '../../styles/headerLinksStyle.js';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { ThemeContext } from "../Theme/ThemeContext";
import LanguageSelectionModal from "../LanguageSelectionModal";

const useStyles = makeStyles((theme) => ({
  ...styles,
  navLink: {
    color: "inherit !important", // Inherit colors from white/dark/transparent parent header
    fontWeight: "600",
    fontSize: "13px",
    textTransform: "uppercase",
    borderRadius: "8px",
    lineHeight: "20px",
    textDecoration: "none",
    margin: "0px",
    display: "inline-flex",
    transition: "all 0.2s ease-in-out",
    "&:hover,&:focus": {
      color: "#E9AE31 !important", // Gold on hover
      background: "rgba(233, 174, 49, 0.1) !important" // Soft gold hover highlight background
    },
    [theme.breakpoints.down("md")]: {
      width: "calc(100% - 30px)",
      marginLeft: "15px",
      marginBottom: "8px",
      marginTop: "8px",
      textAlign: "left"
    }
  },
  icons: {
    width: "18px",
    height: "18px",
    marginRight: "8px"
  },
  socialIcons: {
    width: "18px",
    height: "18px",
  },
  dropdownTrigger: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "8px 12px",
    borderRadius: "8px",
    transition: "all 0.3s",
    "&:hover": {
      background: "rgba(233, 174, 49, 0.1) !important"
    }
  }
}));

export default function HeaderLinks(props) {
  const classes = useStyles();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const languagedata = useSelector(state => state.languagedata);
  const { i18n, t } = useTranslation();
  const [loggedIn, setLoggedIn] = useState(false);
  const navigate = useNavigate();

  const [openLangModal, setOpenLangModal] = useState(false);
  const [langSelection, setLangSelection] = useState();
  const [multiLanguage, setMultiLanguage] = useState();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = (langKey) => {
    setAnchorEl(null);
    if (langKey && multiLanguage) {
      i18n.addResourceBundle(multiLanguage[langKey].langLocale, 'translations', multiLanguage[langKey].keyValuePairs);
      i18n.changeLanguage(multiLanguage[langKey].langLocale);
      setLangSelection(langKey);
      moment.locale(multiLanguage[langKey].dateLocale);
    }
  };

  useEffect(() => {
    if (languagedata.langlist) {
      for (const key of Object.keys(languagedata.langlist)) {
        if (languagedata.langlist[key].langLocale === i18n.language) {
          setLangSelection(key);
        }
      }
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist]);


  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setLoggedIn(true);
    } else {
      setLoggedIn(false);
    }
  }, [auth.profile]);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' }, 
      alignItems: { xs: 'stretch', md: 'center' }, 
      gap: { xs: 1.5, md: 0.5 },
      width: '100%'
    }}>
      <Button
        color="transparent"
        className={classes.navLink}
        onClick={(e) => { e.preventDefault(); navigate('/') }}
      >
        <Home className={classes.icons} />{t('home')}
      </Button>

      {loggedIn && (
        <Button
          color="transparent"
          className={classes.navLink}
          onClick={(e) => {
            e.preventDefault();
            navigate('/bookings');
          }}
        >
          <User className={classes.icons} />
          {t('myaccount')}
        </Button>
      )}

      <Button
        color="transparent"
        className={classes.navLink}
        onClick={(e) => { e.preventDefault(); navigate('/about-us') }}
      >
        <Info className={classes.icons} />{t('about_us')}
      </Button>

      <Button
        color="transparent"
        className={classes.navLink}
        onClick={(e) => { e.preventDefault(); navigate('/contact-us') }}
      >
        <Mail className={classes.icons} />{t('contact_us')}
      </Button>

      {/* Language Selector */}
      <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
        <Button
          color="transparent"
          className={classes.navLink}
          onClick={() => setOpenLangModal(true)}
          style={{ width: '100%' }}
        >
          <Globe className={classes.icons} />
          {i18n.language === 'ar' ? 'العربية' : 'English'}
          <ChevronDown size={14} style={{ marginLeft: '6px' }} />
        </Button>
      </Box>

      <LanguageSelectionModal
        openOverride={openLangModal}
        onCloseOverride={() => setOpenLangModal(false)}
      />

      {/* Theme Toggle */}
      <Button
        color="transparent"
        className={classes.navLink}
        onClick={toggleTheme}
      >
        {isDark ? <Sun className={classes.icons} /> : <Moon className={classes.icons} />}
        {isDark ? t('light_mode', 'Light') : t('dark_mode', 'Dark')}
      </Button>

      {/* Social Links Row */}
      <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 2, my: { xs: 1, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
        {settings && settings.InstagramHandle && (
          <Tooltip title={t('follow_instagram')} placement="top" classes={{ tooltip: classes.tooltip }}>
            <Button href={settings.InstagramHandle} target="_blank" color="transparent" className={classes.navLink} style={{ width: 'auto', margin: 0, padding: '12px' }}>
              <Instagram className={classes.socialIcons} />
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
